/**
 * AGP (Alpha Global Prestige) bespoke storefront design tokens — from the 2026
 * "AGP Website Redesign" handoff. Dark corporate green + gold, Playfair Display
 * headings over Inter body: an editorial/luxury corporate look, replacing the
 * earlier bright JAVA-lime marketplace skin.
 *
 * Hardcoded hex (like upmi/mhyel) — the bespoke PUBLIC pages own their look. The
 * runtime tenant theme (still JAVA green) drives the member portal and the shared
 * transactional surfaces (cart, checkout, product detail), which we deliberately
 * leave alone so logged-in members see no disruption and the live box needs no
 * re-seed.
 */

// ── Palette (handoff :root) ───────────────────────────────────────────────
export const GREEN_DARK = '#0F2318'; // deepest — hero, awards strip, CTA band
export const GREEN_CORP = '#1E3D2B'; // primary corporate green — top bar, headings
export const GREEN_FOREST = '#2F4F3A'; // mid green
export const GOLD = '#D4AF37'; // rules, eyebrows, active underline, primary button
export const GOLD_LIGHT = '#E8D87A'; // hover + headings on dark
export const CHARCOAL = '#2B2B2B'; // body ink
export const WARM_WHITE = '#FAFAF8'; // off-white bands
export const SOFT_BEIGE = '#F2EFE6'; // tinted bands (values, product placeholder)
export const WHITE = '#FFFFFF';
export const TEXT_BODY = '#4B5563';
export const TEXT_MUTED = '#6B7280';

/** Hairline borders — the handoff's rgba(30,61,43,0.07–0.10) over white. */
export const LINE = 'rgba(30,61,43,0.08)';
export const LINE_SOFT = 'rgba(30,61,43,0.07)';

// ── Type ──────────────────────────────────────────────────────────────────
// Loaded via TENANT_PRESETS.agp.theme.fontLinks — gen-tenant-theme.ts bakes the
// <link> into index.html at BUILD time, so no theme/DB change is needed for the
// redesign's fonts to reach the browser.
export const FONT_HEAD = "'Playfair Display', Georgia, serif";
export const FONT_BODY = "'Inter', system-ui, -apple-system, sans-serif";

// ── Brand ─────────────────────────────────────────────────────────────────
export const LOGO = '/tenants/agp-logo.png';

export const TAGLINE = 'Adding Life by Sharing a Cup.';

// Markets bar + contact details moved to the site-content manifest
// (`AGP_SITE` in @mlm/shared — chrome.topbar / contact.office) so the admin
// "Site Pages" tab can edit them; components read them via `useSiteContent`.

// Best-guess AGP socials — confirm with the client; the admin can edit the footer
// links (tenant_config.features.storefront.social) without a code change.
export const SOCIAL = {
  facebook: 'https://facebook.com/alphaglobalprestige',
  youtube: 'https://youtube.com/@alphaglobalprestige',
  instagram: 'https://instagram.com/alphaglobalprestige',
};

/**
 * Public media base — AGP's real photography (~8MB, no video) is committed under
 * `apps/member/public/tenants/agp/` and shipped with the app, so it works on the
 * box with no extra hosting. `VITE_MEDIA_URL` is an optional build-time override
 * if the assets ever move to an R2 public bucket / CDN.
 */
export const MEDIA =
  (import.meta.env.VITE_MEDIA_URL as string | undefined)?.replace(/\/+$/, '') || '/tenants/agp';

// TODO(agp-assets): page imagery now lives in the site-content manifest
// (`AGP_SITE` in @mlm/shared) with the handoff's Unsplash placeholders as
// defaults (client's call to go live with them). The client — or an admin —
// replaces any of them from Storefront → Site Pages (uploads land in R2); the
// real AGP photography committed under `public/tenants/agp/` remains available
// to seed from (main-banner.png · about-pic.png · natural.png · worldwide.png ·
// heart.png · cert1–3.png · bottom1–2.png · more1–4.png · testimony/slide3–14.jpg).

export const fmtPrice = (n: number) =>
  `PHP ${n.toLocaleString('en-PH', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
